import { IonIcon, useIonRouter, useIonViewWillEnter } from "@ionic/react";
import {
  add,
  calendarClearOutline,
  checkmark,
  checkmarkDoneOutline,
  chevronDown,
  createOutline,
  documentTextOutline,
  fileTrayFullOutline,
  folderOpenOutline,
  notificationsOutline,
  searchOutline,
} from "ionicons/icons";
import { FC, ReactElement, useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Redirect } from "react-router-dom";
import styled from "styled-components";
import Info from "../../components/atoms/ErrorComponent";
import ResonanceLoader from "../../components/atoms/ResonanceLoader/ResonanceLoader";
import ResonanceLayout from "../../components/organisms/ResonanceContext/ResonanceLayout";
import { useResonanceLayout } from "../../components/organisms/ResonanceContext/ResonanceLayoutContext";
import { useConfig } from "../../contexts/ConfigContext";
import { useStudySlug } from "../../hooks/useStudySlug";
import { getDefaultTabPath } from "../../navigation/studyTabs";
import { getSuiviGroups, SuiviGroup, SuiviGroupsResponse } from "../../services/suivi.service";
import { getWelcomeInfo } from "../../services/weclome.service";
import { canManageDossiers } from "../../helper/familyComposition";
import { CreateDossierModal } from "./DossierModals";
import { RoleEnum } from "../../types/invitation.types";
import dayjs from "dayjs";
import {
  Chevron,
  CountBadge,
  DueChip,
  EmptyState,
  FoldGlyph,
  IconTile,
  ListCard,
  Page,
  QuietTag,
  Row,
  RowMain,
  RowSub,
  RowTitle,
  SectionHeader,
  SUIVI_CANVAS,
  TonalPill,
} from "./suiviUi";

/**
 * Suivi home — the investigator dashboard's landing. Design contract:
 * one caption sentence is the ENTIRE analytics layer (no KPI tiles/charts),
 * the search pill live-filters the list, groups needing attention lead with a
 * derived action line + the group's WORST-urgency deadline chip, up-to-date
 * groups stay folded. No tab badge, no filter chips.
 */
const SuiviHome: FC = (): ReactElement => {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const ionRouter = useIonRouter();
  const study_slug = useStudySlug();
  const { study, selectedRole } = useConfig();
  const { setResonanceLayout } = useResonanceLayout();

  const [data, setData] = useState<SuiviGroupsResponse | null>(null);
  const [isError, setIsError] = useState(false);
  const [query, setQuery] = useState("");
  const [upToDateOpen, setUpToDateOpen] = useState(false);
  // « Ajouter un dossier » — capability-gated: only when the study has data-only
  // subject roles (from the cached welcome catalog). MentalPlus never shows it.
  const [canCreateDossier, setCanCreateDossier] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);

  const fetchGroups = useCallback((): void => {
    if (!study?.id) return;
    // Never fire the investigator-only request while the self-guard below is
    // redirecting a known non-investigator away (effects run even when the
    // render returned a <Redirect>).
    if (selectedRole && selectedRole.name !== RoleEnum.INVESTIGATOR) return;
    getSuiviGroups({ studyId: study.id, lang: language })
      .then(({ data: response }) => {
        setIsError(false);
        setData(response);
      })
      .catch(() => setIsError(true));
  }, [study?.id, language, selectedRole]);

  // Refetch on EVERY tab entry (stamps/colleague completions must show up):
  // useIonViewWillEnter registers once, so route the call through a ref that is
  // reassigned each render (the DisplayIsland trampoline pattern).
  const willEnterRef = useRef<(() => void) | undefined>(undefined);
  willEnterRef.current = () => {
    setResonanceLayout({
      disableHeader: false,
      disableNav: false,
      backgroundColor: SUIVI_CANVAS,
      title: "suivi_tab_label",
      welcomePage: false,
      headerLeft: undefined, // project default
    });
    fetchGroups();
  };
  useIonViewWillEnter(() => {
    willEnterRef.current?.();
  });

  // First mount also fetches (ionViewWillEnter fires, but keep language changes live).
  useEffect(() => {
    fetchGroups();
  }, [fetchGroups]);

  useEffect(() => {
    let cancelled = false;
    if (!study_slug || !selectedRole) return undefined;
    getWelcomeInfo({ slug: study_slug, lang: language })
      .then(({ data: welcome }) => {
        if (!cancelled) setCanCreateDossier(canManageDossiers(selectedRole, welcome.roles));
      })
      .catch(() => {
        /* fail-soft: the affordance simply stays hidden */
      });
    return () => {
      cancelled = true;
    };
  }, [study_slug, language, selectedRole]);

  // Page self-guard: a KNOWN non-investigator role bounces to its landing tab
  // (the target can never be suivi again — loop-impossible). Unknown role =
  // loader, never a redirect.
  if (selectedRole && selectedRole.name !== RoleEnum.INVESTIGATOR) {
    return <Redirect to={getDefaultTabPath(study_slug, selectedRole.name)} />;
  }

  const groups = data?.groups ?? [];
  const needle = query.trim().toLowerCase();
  const filtered = needle
    ? groups.filter((group) =>
        (group.external_id ?? group.group_key).toLowerCase().includes(needle)
      )
    : groups;

  // ONE predicate and its negation: a dossier whose only outstanding work is a consent
  // to collect must lead (À traiter) — extending only one bucket would list it twice
  // or fold it into « À jour ». The count is the API's (living, non-exited members
  // with an applicable option and nothing recorded); an older API sends none.
  const needsConsent = (group: SuiviGroup): boolean =>
    (group.consent_to_collect_count ?? 0) > 0;
  const needsAttention = (group: SuiviGroup): boolean =>
    group.to_fill_count > 0 || group.awaiting_subject_count > 0 || needsConsent(group);
  const toTreat = filtered.filter(needsAttention);
  const upToDate = filtered.filter((group) => !needsAttention(group));

  const groupLabel = (group: SuiviGroup): string => {
    if (group.external_id) return t("suivi_dossier", { code: group.external_id });
    if (group.members.length === 1 && group.members[0].role_label) {
      return group.members[0].role_label;
    }
    return t("suivi_family");
  };

  // The derived action line — facts come from the API, wording lives here (i18n).
  const actionLine = (group: SuiviGroup): string => {
    const action = group.next_action;
    // Nothing to fill or remind, yet in « À traiter »: the consent is the action.
    if (!action) return needsConsent(group) ? t("suivi_action_collect_consent") : "";
    if (action.kind === "FILL") {
      return t(action.count > 1 ? "suivi_action_fill_plural" : "suivi_action_fill", {
        count: action.count,
      });
    }
    return action.subject_role_label
      ? t("suivi_action_remind_role", { role: action.subject_role_label })
      : t("suivi_action_remind");
  };

  // The row's leading glyph mirrors the action line: fill / remind / consent.
  const actionIcon = (group: SuiviGroup): string => {
    if (!group.next_action) return documentTextOutline;
    return group.next_action.kind === "FILL" ? createOutline : notificationsOutline;
  };

  // « Mercredi 24 septembre » — anchors the relative deadlines (J-3, Demain).
  const todayLabel = ((): string => {
    const formatted = new Intl.DateTimeFormat(language, {
      weekday: "long",
      day: "numeric",
      month: "long",
    }).format(new Date());
    return formatted.charAt(0).toUpperCase() + formatted.slice(1);
  })();

  // Horizon when nothing needs handling: the earliest upcoming window — derived
  // from the SEARCH-FILTERED list so an active search never names an unrelated
  // dossier in the sentence.
  const nextHorizon = filtered
    .filter((group) => group.next_window)
    .sort(
      (first, second) =>
        new Date(first.next_window!.opens_at).getTime()
        - new Date(second.next_window!.opens_at).getTime()
    )[0];

  const openGroup = (group: SuiviGroup): void => {
    ionRouter.push(
      `/${study_slug}/tabs/suivi/groups/${encodeURIComponent(group.group_key)}`,
      "forward",
      "push"
    );
  };

  return (
    <ResonanceLayout>
      <Page>
        {isError ? (
          <Info
            title={t("common_server_error_title")}
            description={t("common_server_error_desc")}
          />
        ) : data === null ? (
          <ResonanceLoader />
        ) : (
          <>
            {/* The caption sentence stays the ENTIRE analytics layer — set larger
                as the page's opening line, under today's date. */}
            <Hero>
              <HeroDate>{todayLabel}</HeroDate>
              <HeroSummary>
                {toTreat.length > 0 ? (
                  <>
                    <b>{t("suivi_caption_todo", { count: toTreat.length })}</b>
                    {" · "}
                    {t("suivi_caption_uptodate", { count: upToDate.length })}
                  </>
                ) : (
                  <b>{t("suivi_caption_all_done", { count: groups.length })}</b>
                )}
              </HeroSummary>
            </Hero>

            <Toolbar>
              <SearchRow>
                <IonIcon icon={searchOutline} aria-hidden="true" />
                <SearchInput
                  type="search"
                  value={query}
                  placeholder={t("suivi_search_placeholder")}
                  onChange={(e) => setQuery(e.target.value)}
                />
              </SearchRow>

              {canCreateDossier && (
                <TonalPill type="button" onClick={() => setCreateOpen(true)}>
                  <IonIcon icon={add} aria-hidden="true" />
                  {t("suivi_new_dossier_button")}
                </TonalPill>
              )}
            </Toolbar>

            {filtered.length === 0 ? (
              <EmptyState icon={needle ? searchOutline : folderOpenOutline}>
                {needle
                  ? t("suivi_search_no_match", { query: query.trim() })
                  : t("suivi_empty")}
              </EmptyState>
            ) : (
              <>
                {toTreat.length > 0 && (
                  <>
                    <SectionHeader
                      title={t("suivi_group_todo_title")}
                      icon={fileTrayFullOutline}
                      count={toTreat.length}
                    />
                    <ListCard>
                      {toTreat.map((group) => (
                        <GroupRow
                          key={group.group_key}
                          as="button"
                          type="button"
                          $tappable
                          onClick={() => openGroup(group)}
                        >
                          <GroupTile aria-hidden="true">
                            <IonIcon icon={actionIcon(group)} />
                          </GroupTile>
                          <GroupTitle>{groupLabel(group)}</GroupTitle>
                          {group.worst_end_date && (
                            <GroupDue>
                              <DueChip endDate={group.worst_end_date} t={t} />
                            </GroupDue>
                          )}
                          <GroupSub>
                            <span>{actionLine(group)}</span>
                            {/* Only beside a fill/remind action — when the consent IS
                                the action line, repeating the same words is noise. */}
                            {group.next_action && needsConsent(group) && (
                              <QuietTag $tone="info">
                                {t("suivi_consent_to_collect_tag")}
                              </QuietTag>
                            )}
                          </GroupSub>
                          <GroupChevron>
                            <Chevron />
                          </GroupChevron>
                        </GroupRow>
                      ))}
                    </ListCard>
                  </>
                )}

                {toTreat.length === 0 && nextHorizon?.next_window && (
                  <HorizonLine>
                    <IonIcon icon={calendarClearOutline} aria-hidden="true" />
                    {t("suivi_home_next_window", {
                      date: dayjs(nextHorizon.next_window.opens_at).format("DD/MM/YYYY"),
                      group: groupLabel(nextHorizon),
                    })}
                  </HorizonLine>
                )}

                {upToDate.length > 0 && (
                  <ListCard>
                    <Row
                      as="button"
                      type="button"
                      $tappable
                      aria-expanded={upToDateOpen}
                      onClick={() => setUpToDateOpen((open) => !open)}
                    >
                      <IconTile $tone="success" aria-hidden="true">
                        <IonIcon icon={checkmarkDoneOutline} />
                      </IconTile>
                      <RowMain>
                        <RowTitle>{t("suivi_group_uptodate_title")}</RowTitle>
                      </RowMain>
                      <CountBadge>{upToDate.length}</CountBadge>
                      <FoldGlyph $open={upToDateOpen} icon={chevronDown} aria-hidden="true" />
                    </Row>
                    {upToDateOpen &&
                      upToDate.map((group) => (
                        <Row
                          key={group.group_key}
                          as="button"
                          type="button"
                          $tappable
                          onClick={() => openGroup(group)}
                        >
                          <IconTile $tone="neutral" aria-hidden="true">
                            <IonIcon icon={checkmark} />
                          </IconTile>
                          <RowMain>
                            <RowTitle>{groupLabel(group)}</RowTitle>
                            <RowSub>{t("suivi_up_to_date_row")}</RowSub>
                          </RowMain>
                          <Chevron />
                        </Row>
                      ))}
                  </ListCard>
                )}
              </>
            )}
          </>
        )}

        <CreateDossierModal
          isOpen={createOpen}
          onDismiss={() => setCreateOpen(false)}
          onCreated={(groupKey) => {
            setCreateOpen(false);
            fetchGroups();
            ionRouter.push(
              `/${study_slug}/tabs/suivi/groups/${encodeURIComponent(groupKey)}`,
              "forward",
              "push"
            );
          }}
        />
      </Page>
    </ResonanceLayout>
  );
};

const Hero = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 6px 4px 2px;
`;

const HeroDate = styled.p`
  margin: 0;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--app-color-primary, #2563eb);
`;

const HeroSummary = styled.p`
  margin: 0;
  font-family: var(--app-font-heading, inherit);
  font-size: 24px;
  font-weight: 600;
  line-height: 1.25;
  letter-spacing: -0.01em;
  color: rgba(var(--app-color-text-rgb, 15, 23, 42), 0.5);

  b {
    font-weight: 700;
    color: var(--app-color-text, #0f172a);
  }
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 4px;
`;

/* A label, so a tap anywhere on the pill (icon included) focuses the field. */
const SearchRow = styled.label`
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 10px;
  height: 44px;
  padding: 0 16px;
  border-radius: 999px;
  background: var(--app-color-surface-tint, #ffffff);
  border: 1px solid rgba(var(--app-color-text-rgb, 15, 23, 42), 0.09);
  box-shadow:
    0 1px 2px rgba(var(--app-color-text-rgb, 15, 23, 42), 0.04),
    0 10px 24px -18px rgba(var(--app-color-text-rgb, 15, 23, 42), 0.35);
  color: rgba(var(--app-color-text-rgb, 15, 23, 42), 0.45);
  cursor: text;
  transition:
    border-color 0.15s ease,
    box-shadow 0.15s ease,
    color 0.15s ease;

  ion-icon {
    flex: none;
    font-size: 19px;
  }

  &:focus-within {
    border-color: rgba(var(--app-color-primary-rgb, 37, 99, 235), 0.55);
    box-shadow: 0 0 0 4px rgba(var(--app-color-primary-rgb, 37, 99, 235), 0.12);
    color: var(--app-color-primary, #2563eb);
  }
`;

const SearchInput = styled.input`
  flex: 1;
  min-width: 0;
  border: none;
  outline: none;
  background: transparent;
  font-family: inherit;
  font-size: 15px;
  color: var(--app-color-text, #0f172a);

  &::placeholder {
    color: rgba(var(--app-color-text-rgb, 15, 23, 42), 0.45);
  }
`;

/* « À traiter » row, inbox-style: dossier + worst deadline share the first line,
   the action line (and its consent tag) gets the full width underneath instead of
   being squeezed beside the chip. */
const GroupRow = styled(Row)`
  display: grid;
  grid-template-columns: 40px minmax(0, 1fr) auto auto;
  grid-template-areas:
    "tile title due chevron"
    "tile sub sub chevron";
  column-gap: 12px;
  row-gap: 4px;
  align-items: center;
`;

const GroupTile = styled(IconTile)`
  grid-area: tile;
`;

const GroupTitle = styled(RowTitle)`
  grid-area: title;
`;

const GroupDue = styled.span`
  grid-area: due;
  display: inline-flex;
`;

const GroupSub = styled(RowSub)`
  grid-area: sub;
  margin-top: 0;
`;

const GroupChevron = styled.span`
  grid-area: chevron;
  display: inline-flex;
`;

/* « Prochaine fenêtre » when nothing is left to handle — one quiet sentence. */
const HorizonLine = styled.p`
  align-self: center;
  margin: 2px 0;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  border-radius: 999px;
  font-size: 13px;
  line-height: 1.35;
  text-align: center;
  background: rgba(var(--app-color-primary-rgb, 37, 99, 235), 0.07);
  color: rgba(var(--app-color-text-rgb, 15, 23, 42), 0.7);

  ion-icon {
    flex: none;
    font-size: 15px;
    color: var(--app-color-primary, #2563eb);
  }
`;

export default SuiviHome;
